import React, { useState } from "react";
import { Outlet, Link, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "../../contexts/AuthContext";
import { useUnifiedDashboard } from "../../contexts/UnifiedDashboardContext";
import { Bell, Menu, X, LogOut, User, AlertTriangle, Lock, ClipboardCheck, ChevronRight } from "lucide-react";
import { Transition } from "@headlessui/react";
import PKFLogo from "./PKFLogo";
import ChangePassword from "../ChangePassword";
import UpdateBanner from "./UpdateBanner";
import { isNotLivePath, NotLiveBanner } from "./NotLive";
import { APP_VERSION, BUILD_TIME } from "../../version";
import { formatDateTime } from "../../utils/dateFormat";
import { IS_STAGING } from "../../utils/siteEnv";
import { WORKFLOW_CHANGED_EVENT } from "../../utils/api";

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001';

// Management screens that live under a module's URL prefix. Reached from the
// Management hub (/manage), so the top nav must keep *Management* highlighted —
// a plain prefix match would light up Professional Development for /ld/records.
// /manage and /settings/* are management by prefix.
// `/qm/records` 整個子樹都是管理頁（CW/CC・FS・QC35 記錄的清單與詳情），
// 所以走前綴——它有 /:kind 與 /:kind/:id 兩層，逐條列進
// MANAGE_PATHS 只會漏掉詳情頁（那是精確比對）。
const MANAGE_PREFIXES = ['/manage', '/settings', '/qm/records', '/qm/qc-records', '/qm/engagements', '/ld/quizzes'];
const MANAGE_PATHS = new Set([
  '/qm/engagements', '/qm/qc-records', '/qm/staff', '/qm/monthly-records',
  '/qm/registers', '/qm/adaptive-cookie', '/qm/chargeable-import', '/qm/timesheet-check',
  '/ld/records', '/ld/records/development', '/ld/setup', '/ld/quizzes', '/ld/reminders',
]);

const isManagePath = (pathname) =>
  MANAGE_PATHS.has(pathname)
  || MANAGE_PREFIXES.some((p) => pathname === p || pathname.startsWith(`${p}/`));

/**
 * Which top-nav item owns the current URL. Management wins for every screen
 * reached from the Management hub; everything else is the usual prefix match.
 */
const navIsActive = (item, pathname) => {
  if (isManagePath(pathname)) return item.href === '/manage';
  return pathname === item.href
    || (item.href !== '/' && pathname.startsWith(`${item.href}/`));
};

const Layout = ({ children }) => {
  const { user, logout } = useAuth();
  const { alerts: contextAlerts } = useUnifiedDashboard();
  const location = useLocation();
  const navigate = useNavigate();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isProfileOpen, setIsProfileOpen] = useState(false);
  const [isNotificationOpen, setIsNotificationOpen] = useState(false);
  const [alerts, setAlerts] = useState([]);
  const [showChangePassword, setShowChangePassword] = useState(false);
  // Per-queue pending counts so the bell can link each review queue directly.
  // ONE feed for every queue (docs/workflow-contract.md §2, 接點 4) — the
  // endpoint returns null for queues this user may not review, so a submitter
  // simply sees no badges.
  const [wfCounts, setWfCounts] = useState({
    engreg: 0, cwcc: 0, esc: 0, screening: 0, fs: 0, qc35: 0, ear: 0, client: 0, qc: 0, qca: 0, qcad: 0, car: 0, fsa: 0, qc35a: 0,
    cpd: 0, pq: 0, absence: 0, dispute: 0,
  });
  // Requester-facing feed: my own requests a reviewer just approved / returned
  // (GET /api/workflow/my-updates). `updatesUnseen` drives the
  // red dot until the bell is opened; the list itself lingers in the dropdown
  // until the next navigation refetch (by then they're below the watermark).
  const [myUpdates, setMyUpdates] = useState([]);
  const [updatesUnseen, setUpdatesUnseen] = useState(false);
  // Bumped by fetchJSON after any successful write to a workflow route
  // (approve, return, submit, delete…) so both feeds refetch right away —
  // acting on a request stays on the same page, so navigation alone would
  // leave the badge stale until the next click.
  const [wfTick, setWfTick] = useState(0);
  React.useEffect(() => {
    const bump = () => setWfTick((t) => t + 1);
    window.addEventListener(WORKFLOW_CHANGED_EVENT, bump);
    return () => window.removeEventListener(WORKFLOW_CHANGED_EVENT, bump);
  }, []);
  const isSuperAdmin = user?.role === 'super_admin';
  const perms = user?.permissions || [];
  const canManageUsers = isSuperAdmin || perms.includes('admin:users');
  // The partner-reviewed flows (QC/FS/QC35 + amendments) have NO
  // review key — a partner's queue comes from being a lead on the covered
  // engagements, which only the SERVER can resolve. So the badge feed is
  // fetched for EVERYONE; the server returns null counts for queues the
  // caller cannot review, and the total is simply the sum.
  // Re-checked on navigation so acting updates the count.
  React.useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch(`${API_BASE_URL}/api/workflow/pending-count`, { credentials: 'include' });
        const data = await res.json().catch(() => ({}));
        if (!cancelled && res.ok) setWfCounts({
          engreg: data.engreg || 0, cwcc: data.cwcc || 0, esc: data.esc || 0, screening: data.screening || 0, fs: data.fs || 0, qc35: data.qc35 || 0,
          ear: data.ear || 0, client: data.client || 0, qc: data.qc || 0,
          qca: data.qca || 0, qcad: data.qcad || 0, car: data.car || 0, fsa: data.fsa || 0, qc35a: data.qc35a || 0,
          cpd: data.cpd || 0, pq: data.pq || 0, absence: data.absence || 0, dispute: data.dispute || 0,
        });
      } catch (e) { /* badge is best-effort */ }
    })();
    return () => { cancelled = true; };
  }, [location.pathname, wfTick]);

  // Requester feed — every logged-in user (returns only their own rows).
  // Re-checked on navigation so acting on a request refreshes the dot.
  React.useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch(`${API_BASE_URL}/api/workflow/my-updates`, { credentials: 'include' });
        const data = await res.json().catch(() => ({}));
        if (!cancelled && res.ok) {
          const updates = data.updates || [];
          setMyUpdates(updates);
          setUpdatesUnseen(updates.length > 0);
        }
      } catch (e) { /* feed is best-effort */ }
    })();
    return () => { cancelled = true; };
  }, [location.pathname, wfTick]);

  // Opening the bell acknowledges the requester feed: bump the server watermark
  // and drop the local dot. The listed items stay visible until the next
  // navigation refetch (which will then return nothing new).
  const openNotifications = () => {
    const next = !isNotificationOpen;
    setIsNotificationOpen(next);
    if (next && updatesUnseen) {
      setUpdatesUnseen(false);
      fetch(`${API_BASE_URL}/api/workflow/my-updates/seen`, { method: 'POST', credentials: 'include' }).catch(() => {});
    }
  };

  const pendingTotal = Object.values(wfCounts).reduce((sum, n) => sum + (n || 0), 0);

  // Filter navigation based on user permissions (docs/extending.md: a new
  // module needs its link here + ENABLED_MODULES + App.js route +
  // ManageData card).
  const getFilteredNavigation = () => {
    // Overview answers "what needs me now"; My Records answers "what is mine"
    // — both are personal, so they sit together at the front and neither is
    // gated (everyone gets My Records; the server scopes it).
    const baseNav = [
      { name: 'Overview', href: '/' },
      { name: 'My Records', href: '/my' },
    ];
    const userPermissions = user?.permissions || [];

    // A dashboard link shows only when the caller holds the VIEW key that
    // page actually requires — NOT any key sharing the module prefix.
    // The ld/qm prefixes also carry submit/review/edit keys (everyone
    // holds ld:submit), so prefix matching would show "Professional
    // Development" to users whose click lands on Access denied.
    const canView = (perm) => isSuperAdmin || userPermissions.includes(perm);

    if (canView('qm:view')) {
      baseNav.push({ name: 'Quality Management', href: '/qm' });
    }

    if (canView('ld:view')) {
      baseNav.push({ name: 'Professional Development', href: '/ld' });
    }

    // Client Hub — the firm-wide client lookup (read-only). Every account
    // holds clients:hub:view by default; the key exists so it can be revoked.
    if (canView('clients:hub:view')) {
      baseNav.push({ name: 'Client Hub', href: '/clients' });
    }

    // Workflow — firm process requests. Visible to every logged-in user:
    // the hub's Professional Development section carries self-service flows
    // (External CPD Declaration) that need no permission.
    baseNav.push({ name: 'Workflow', href: '/workflow', badge: pendingTotal });

    // Management — visible to anyone holding at least one management key
    // (one /manage card = one key; admin:users and
    // settings:notifications:edit included — their cards live under System).
    // Every management key ends in ':edit', so the suffix is the test.
    if (isSuperAdmin
      || userPermissions.some((p) => p.endsWith(':edit'))
      || userPermissions.includes('admin:users')) {
      baseNav.push({ name: 'Management', href: '/manage' });
    }

    return baseNav;
  };

  const navigation = getFilteredNavigation();

  // Requester feed rendered as clickable alerts, shown above the review queues.
  const updateAlerts = myUpdates.map((u) => ({
    message: u.status === 'awaiting_info'
      ? `${u.requestNo} was returned — please review and resubmit`
      : `${u.requestNo} was approved`,
    href: u.href,
    timestamp: u.statusAt,
  }));
  const displayAlerts = [...updateAlerts, ...alerts];
  // Red dot: reviewer has pending items, or I have unacknowledged updates.
  const showBellDot = alerts.length > 0 || updatesUnseen;

  // Sync alerts from context + workflow review queues. Each review queue
  // becomes its own clickable entry that jumps straight to that queue's list
  // (reviewers land on the "Pending review only" view there), so a reviewer
  // knows exactly which workflow has items and reaches it in one click.
  React.useEffect(() => {
    const plural = (n) => (n > 1 ? 's' : '');
    const reviewAlerts = [
      { n: wfCounts.client, href: '/workflow/client', label: (n) => `${n} New Client request${plural(n)} awaiting your approval` },
      { n: wfCounts.car, href: '/workflow/car', label: (n) => `${n} Client Amendment request${plural(n)} awaiting your approval` },
      { n: wfCounts.engreg, href: '/workflow/engreg', label: (n) => `${n} Engagement Registration request${plural(n)} awaiting your approval` },
      { n: wfCounts.qc, href: '/workflow/qc', label: (n) => `${n} QC6 / QC7 Forms request${plural(n)} awaiting your approval` },
      { n: wfCounts.cwcc, href: '/workflow/cwcc', label: (n) => `${n} CaseWare, Charge Code & Team Update request${plural(n)} awaiting your action` },
      { n: wfCounts.fs, href: '/workflow/fs', label: (n) => `${n} Signed Financial Statements submission${plural(n)} awaiting your approval` },
      { n: wfCounts.qc35, href: '/workflow/qc35', label: (n) => `${n} QC35 Form submission${plural(n)} awaiting your approval` },
      { n: wfCounts.esc, href: '/workflow/esc', label: (n) => `${n} Engagement Status Change request${plural(n)} awaiting review` },
      { n: wfCounts.screening, href: '/workflow/screening', label: (n) => `${n} Screening request${plural(n)} awaiting QM` },
      { n: wfCounts.ear, href: '/workflow/ear', label: (n) => `${n} Engagement Amendment request${plural(n)} awaiting your approval` },
      { n: wfCounts.qca, href: '/workflow/qca', label: (n) => `${n} QC Amendment request${plural(n)} awaiting your approval` },
      { n: wfCounts.qcad, href: '/workflow/qcad', label: (n) => `${n} QC6/QC7 Addendum request${plural(n)} awaiting your approval` },
      { n: wfCounts.fsa, href: '/workflow/fsa', label: (n) => `${n} Signed Financial Statements Amendment request${plural(n)} awaiting your approval` },
      { n: wfCounts.qc35a, href: '/workflow/qc35a', label: (n) => `${n} QC35 Form Amendment request${plural(n)} awaiting your approval` },
      { n: wfCounts.cpd, href: '/workflow/cpd', label: (n) => `${n} External CPD declaration${plural(n)} awaiting review` },
      { n: wfCounts.pq, href: '/workflow/pq', label: (n) => `${n} professional qualification declaration${plural(n)} awaiting review` },
      { n: wfCounts.absence, href: '/workflow/absence', label: (n) => `${n} training absence declaration${plural(n)} awaiting review` },
      { n: wfCounts.dispute, href: '/workflow/dispute', label: (n) => `${n} attendance dispute${plural(n)} awaiting review` },
    ]
      .filter((q) => q.n > 0)
      .map((q) => ({ message: q.label(q.n), href: q.href, timestamp: null }));

    setAlerts([...reviewAlerts, ...(contextAlerts || [])]);
  }, [contextAlerts, wfCounts]);

  // Close dropdowns when clicking outside
  React.useEffect(() => {
    const handleClickOutside = (event) => {
      if (isProfileOpen && !event.target.closest('.profile-dropdown-container')) {
        setIsProfileOpen(false);
      }
      if (isNotificationOpen && !event.target.closest('.notification-dropdown-container')) {
        setIsNotificationOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isProfileOpen, isNotificationOpen]);

  const handleLogout = async () => {
    try {
      await logout();
      navigate('/login');
    } catch (error) {
      console.error("Failed to log out", error);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50 flex flex-col">
      {/* New-version notice (only renders when a newer build is deployed) */}
      <UpdateBanner />

      {/* Top Navigation Bar */}
      <nav className="bg-white border-b border-slate-200 h-16 flex items-center justify-between px-4 sm:px-6 shrink-0 z-20 relative shadow-sm">
        <div className="flex items-center gap-8">
            {/* Logo */}
            <Link to="/" className="flex items-center gap-3">
                <PKFLogo className="h-8 w-auto" />
                <span className="text-lg font-semibold text-slate-700 border-l border-slate-300 pl-3 hidden sm:inline">Dashboard Portal</span>
                {IS_STAGING && (
                    <span className="px-1.5 py-0.5 rounded border border-amber-400 text-amber-600 text-xs font-medium">
                        Staging
                    </span>
                )}
            </Link>

            {/* Desktop Navigation */}
            <div className="hidden md:flex items-center gap-1">
                {navigation.map((item) => {
                    const isActive = navIsActive(item, location.pathname);
                    if (item.disabled) {
                        return (
                            <span
                                key={item.name}
                                title="Temporarily unavailable"
                                className="px-3 py-2 text-sm font-medium rounded-md inline-flex items-center gap-1.5 text-slate-300 cursor-not-allowed select-none"
                            >
                                {item.name}
                            </span>
                        );
                    }
                    return (
                        <Link
                            key={item.name}
                            to={item.href}
                            className={`px-3 py-2 text-sm font-medium rounded-md transition-colors inline-flex items-center gap-1.5 ${
                                isActive
                                    ? 'text-blue-600 bg-blue-50'
                                    : 'text-slate-600 hover:text-slate-900 hover:bg-slate-50'
                            }`}
                        >
                            {item.name}
                            {item.badge > 0 && (
                                <span className="inline-flex items-center justify-center min-w-[1.1rem] h-[1.1rem] px-1 rounded-full bg-red-500 text-white text-[10px] font-semibold leading-none">
                                    {item.badge}
                                </span>
                            )}
                        </Link>
                    );
                })}
            </div>
        </div>

        {/* Right Side Actions */}
        <div className="flex items-center gap-2 sm:gap-4">
            {/* Notification Bell */}
            <div className="relative notification-dropdown-container">
                <button
                    onClick={openNotifications}
                    className="p-2 text-slate-400 hover:text-slate-600 rounded-full hover:bg-slate-100 relative"
                >
                    <Bell className="w-5 h-5" />
                    {showBellDot && (
                        <span className="absolute top-1 right-1 h-2 w-2 bg-red-500 rounded-full"></span>
                    )}
                </button>

                {/* Notification Dropdown */}
                <Transition
                    show={isNotificationOpen}
                    enter="transition ease-out duration-100"
                    enterFrom="transform opacity-0 scale-95"
                    enterTo="transform opacity-100 scale-100"
                    leave="transition ease-in duration-75"
                    leaveFrom="transform opacity-100 scale-100"
                    leaveTo="transform opacity-0 scale-95"
                >
                    <div className="absolute right-0 mt-2 w-80 rounded-lg shadow-lg bg-white ring-1 ring-black ring-opacity-5 focus:outline-none z-50">
                        <div className="px-4 py-3 border-b border-gray-100">
                            <h3 className="text-sm font-semibold text-gray-900 flex items-center gap-2">
                                <Bell className="w-4 h-4" />
                                Active Alerts
                            </h3>
                        </div>
                        <div className="max-h-96 overflow-y-auto">
                            {displayAlerts.length > 0 ? (
                                <div className="py-2">
                                    {displayAlerts.map((alert, index) =>
                                        alert.href ? (
                                            <button
                                                key={index}
                                                onClick={() => {
                                                    setIsNotificationOpen(false);
                                                    navigate(alert.href);
                                                }}
                                                className="w-full text-left px-4 py-3 hover:bg-blue-50 border-b border-gray-50 last:border-b-0 group"
                                            >
                                                <div className="flex items-start gap-3">
                                                    <ClipboardCheck className="w-4 h-4 text-blue-600 mt-0.5 flex-shrink-0" />
                                                    <p className="flex-1 min-w-0 text-sm text-gray-700 leading-relaxed">
                                                        {alert.message}
                                                    </p>
                                                    <ChevronRight className="w-4 h-4 text-gray-300 group-hover:text-blue-500 mt-0.5 flex-shrink-0" />
                                                </div>
                                            </button>
                                        ) : (
                                            <div key={index} className="px-4 py-3 hover:bg-yellow-50 border-b border-gray-50 last:border-b-0">
                                                <div className="flex items-start gap-3">
                                                    <AlertTriangle className="w-4 h-4 text-yellow-600 mt-0.5 flex-shrink-0" />
                                                    <div className="flex-1 min-w-0">
                                                        <p className="text-sm text-gray-700 leading-relaxed">
                                                            {alert.message}
                                                        </p>
                                                        {alert.timestamp && (
                                                            <p className="text-xs text-gray-500 mt-1">
                                                                {formatDateTime(alert.timestamp)}
                                                            </p>
                                                        )}
                                                    </div>
                                                </div>
                                            </div>
                                        )
                                    )}
                                </div>
                            ) : (
                                <div className="px-4 py-8 text-center">
                                    <p className="text-sm text-gray-500">No active alerts</p>
                                </div>
                            )}
                        </div>
                    </div>
                </Transition>
            </div>
            
            {/* User Profile Dropdown */}
            <div className="relative profile-dropdown-container">
                <button 
                    onClick={() => setIsProfileOpen(!isProfileOpen)}
                    className="h-8 w-8 rounded-full bg-slate-200 flex items-center justify-center text-slate-600 font-medium text-sm border border-slate-300 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                >
                    {user?.email ? user.email.charAt(0).toUpperCase() : <User className="w-4 h-4" />}
                </button>

                {/* Dropdown Menu */}
                <Transition
                    show={isProfileOpen}
                    enter="transition ease-out duration-100"
                    enterFrom="transform opacity-0 scale-95"
                    enterTo="transform opacity-100 scale-100"
                    leave="transition ease-in duration-75"
                    leaveFrom="transform opacity-100 scale-100"
                    leaveTo="transform opacity-0 scale-95"
                >
                    <div className="absolute right-0 mt-2 w-56 rounded-md shadow-lg bg-white ring-1 ring-black ring-opacity-5 py-1 focus:outline-none">
                        <div className="px-4 py-2 text-xs text-gray-500 border-b border-gray-100">
                            Signed in as<br/>
                            <span className="font-medium text-gray-900 truncate block">{user?.email}</span>
                            {canManageUsers && (
                                <span className="inline-flex items-center mt-1.5 px-2 py-0.5 rounded text-xs font-medium bg-blue-100 text-blue-800">
                                    Administrator
                                </span>
                            )}
                        </div>
                        <button
                            onClick={() => {
                                setShowChangePassword(true);
                                setIsProfileOpen(false);
                            }}
                            className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 flex items-center gap-2"
                        >
                            <Lock className="w-4 h-4" />
                            Change Password
                        </button>
                        <button
                            onClick={handleLogout}
                            className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 flex items-center gap-2 border-t border-gray-100"
                        >
                            <LogOut className="w-4 h-4" />
                            Sign Out
                        </button>
                    </div>
                </Transition>
            </div>

            {/* Mobile Menu Button */}
            <div className="md:hidden flex items-center">
                <button
                    onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
                    className="p-2 rounded-md text-slate-400 hover:text-slate-500 hover:bg-slate-100 focus:outline-none focus:ring-2 focus:ring-inset focus:ring-blue-500"
                >
                    {isMobileMenuOpen ? (
                        <X className="block h-6 w-6" aria-hidden="true" />
                    ) : (
                        <Menu className="block h-6 w-6" aria-hidden="true" />
                    )}
                </button>
            </div>
        </div>
      </nav>

      {/* Mobile Menu */}
      {isMobileMenuOpen && (
        <div className="md:hidden bg-white border-b border-slate-200">
            <div className="px-2 pt-2 pb-3 space-y-1 sm:px-3">
                {navigation.map((item) => {
                    const isActive = navIsActive(item, location.pathname);
                    if (item.disabled) {
                        return (
                            <span
                                key={item.name}
                                title="Temporarily unavailable"
                                className="block px-3 py-2 rounded-md text-base font-medium text-slate-300 cursor-not-allowed select-none"
                            >
                                {item.name}
                            </span>
                        );
                    }
                    return (
                        <Link
                            key={item.name}
                            to={item.href}
                            onClick={() => setIsMobileMenuOpen(false)}
                            className={`block px-3 py-2 rounded-md text-base font-medium ${
                                isActive
                                    ? 'bg-blue-50 text-blue-600'
                                    : 'text-slate-600 hover:text-slate-900 hover:bg-slate-50'
                            }`}
                        >
                            {item.name}
                            {item.badge > 0 && (
                                <span className="ml-2 inline-flex items-center justify-center min-w-[1.1rem] h-[1.1rem] px-1 rounded-full bg-red-500 text-white text-[10px] font-semibold leading-none">
                                    {item.badge}
                                </span>
                            )}
                        </Link>
                    );
                })}
            </div>
        </div>
      )}

      {/* Main Content */}
      <main className="flex-1 overflow-auto">
        {isNotLivePath(location.pathname) && (
          <div className="px-4 sm:px-6 pt-4">
            <NotLiveBanner />
          </div>
        )}
        {children || <Outlet />}
      </main>

      {/* Change Password Modal */}
      {showChangePassword && (
        <ChangePassword
          onClose={() => setShowChangePassword(false)}
          onSuccess={() => {
            console.log("Password changed successfully");
          }}
        />
      )}

      {/* Footer */}
      <footer className="bg-white border-t border-slate-200 py-4 px-6 text-center text-sm text-slate-500 shrink-0">
        &copy; {new Date().getFullYear()} PKF. All rights reserved.
        <span className="mx-2 text-slate-300">&middot;</span>
        <span title={`Build ${BUILD_TIME}`}>v{APP_VERSION}</span>
      </footer>
    </div>
  );
};

export default Layout;